import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
import { AdditionalStepsService } from '../../../../../../core/services/additional-steps.service';
import { AdditionalStep } from '../../../../../../core/models/additional-step.model';
import { AUTHORIZED_COMMUNICATION_METHODS } from '../../../../../../core/lists/_models/task-ai-fields';

@Component({
  selector: 'kt-task-template-ai-tab',
  templateUrl: './task-template-ai-tab.component.html',
  styleUrls: ['./task-template-ai-tab.component.scss'],
})
export class TaskTemplateAiTabComponent implements OnInit, OnChanges {
  @Input() form: FormGroup;
  @Input() templateId: number;
  @Input() readOnly = false;
  @Output() saveTemplateAi = new EventEmitter<void>();

  communicationMethods = AUTHORIZED_COMMUNICATION_METHODS;
  stepsForm: FormGroup;
  steps: AdditionalStep[] = [];
  loading = false;
  error = '';
  savingSteps = false;

  constructor(
    private fb: FormBuilder,
    private additionalStepsService: AdditionalStepsService,
    private layoutUtils: LayoutUtilsService,
  ) {}

  get stepControls(): FormArray {
    return this.stepsForm.get('steps') as FormArray;
  }

  templateControl(name: string): FormControl {
    return this.form?.get(name) as FormControl;
  }

  ngOnInit(): void {
    this.stepsForm = this.fb.group({ steps: this.fb.array([]) });
    this.loadSteps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.templateId && !changes.templateId.firstChange) {
      this.loadSteps();
    }
    if (changes.readOnly && this.stepsForm) {
      this.applyReadOnly();
    }
  }

  save(): void {
    if (this.readOnly) {
      return;
    }
    this.saveTemplateAi.emit();
    this.saveSteps();
  }

  private loadSteps(): void {
    if (!this.templateId) {
      this.steps = [];
      return;
    }
    this.loading = true;
    this.error = '';
    this.additionalStepsService.getAdditionalSteps(this.templateId).subscribe(
      (response) => {
        this.loading = false;
        this.steps = response?.data?.additional_step_template_dtos || [];
        this.buildStepForms();
      },
      (error: HttpErrorResponse) => {
        this.loading = false;
        this.error =
          error?.status === 403
            ? 'You do not have permission to view Task Template AI fields.'
            : 'Unable to load step AI fields.';
      },
    );
  }

  private buildStepForms(): void {
    const array = this.fb.array(
      this.steps.map((step) =>
        this.fb.group({
          id: [step.id],
          objective: [step['objective'] || ''],
          critical_data: [step['critical_data'] || ''],
          data_sources: [step['data_sources'] || ''],
          authorized_communication_methods: [
            step['authorized_communication_methods'] || [],
          ],
        }),
      ),
    );
    this.stepsForm.setControl('steps', array);
    this.applyReadOnly();
  }

  private applyReadOnly(): void {
    if (this.readOnly) {
      this.stepsForm?.disable({ emitEvent: false });
    } else {
      this.stepsForm?.enable({ emitEvent: false });
    }
  }

  private saveSteps(): void {
    if (this.readOnly || !this.stepControls?.length) {
      return;
    }
    this.savingSteps = true;
    const updates = this.stepControls.controls.map((control, index) => {
      const value = control.value;
      const step = this.steps[index];
      const body = {
        order: step.order,
        task_template_id: step.task_template?.id,
        sequential_order: step.sequential_order || false,
        is_externally_completable: step.is_externally_completable || false,
        objective: value.objective || '',
        critical_data: value.critical_data || '',
        data_sources: value.data_sources || '',
        authorized_communication_methods: value.authorized_communication_methods || [],
      };
      return this.additionalStepsService.updateAdditionalSteps(body, step.id).toPromise();
    });
    Promise.all(updates)
      .then(() => {
        this.savingSteps = false;
        this.layoutUtils.showActionNotification(
          'Step AI fields saved',
          MessageType.Update,
          3000,
          false,
          false,
        );
      })
      .catch((error: HttpErrorResponse) => {
        this.savingSteps = false;
        this.error =
          error?.status === 403
            ? 'You do not have permission to edit Task Template AI fields.'
            : 'Unable to save step AI fields.';
      });
  }
}
